import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { Subject, ReplaySubject, fromEvent } from 'rxjs';
import { map } from 'rxjs/operators';

var ChannelType;
(function (ChannelType) {
    /**
     * When connected to this type of channel, you'll receive an event for each change.
     * Useful for communicating events/actions between fronts, like a request to logout...
     */
    ChannelType["event"] = "EVENT";
    /**
     * When connected to this type of channel, you'll receive an event with the last emitted value
     * (if one was ever emitted) and then an event for each change.
     * Useful for communicating state between fronts, like an auth token...
     */
    ChannelType["replayLastValue"] = "REPLAY_LAST_VALUE";
})(ChannelType || (ChannelType = {}));

class EventBusService {
    constructor() {
        this.subscriptions = [];
    }
    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
    /**
     * Connect to a channel (or create it if never connected by another front)
     *
     * @param namespace the channel namespace.
     * @param id the channel name.
     * @param config the channel config.
     * @return the channel emitter subject and receiver observable.
     */
    connectToChannel(namespace, id, config) {
        const channelId = `${namespace}#${id}`;
        const channelConfig = this.getSettings().channelConfigs[channelId];
        if (channelConfig) {
            if (channelConfig.type === config.type) {
                return this.getChannelEmitterAndReceiver(channelId, config);
            }
            else {
                throw new Error(`The channel config of channel ${channelId} does not matched what another front defined!`);
            }
        }
        else {
            this.getSettings().channelConfigs[channelId] = config;
            return this.getChannelEmitterAndReceiver(channelId, config);
        }
    }
    getChannelEmitterAndReceiver(channelId, config) {
        const eventType = `eventbus_${channelId}`;
        const emitter = new Subject();
        // Even for non-replay channels we store the last value for debugging purposes.
        this.subscriptions.push(emitter.subscribe((value) => {
            this.getSettings().channelLastValues[channelId] = value;
            // Send the event.
            const event = new CustomEvent(eventType, { detail: value });
            window.dispatchEvent(event);
        }));
        // To make the receiver, create a ReplaySubject with 1 replay if type === REPLAY so
        // that subscribing after a value emitted replays the last value.
        // For type === EVENT, a simple Subject is enough.
        const receiverSubject = config.type === ChannelType.replayLastValue
            ? new ReplaySubject(1)
            : new Subject();
        // For REPLAY channels, first emit the last known value in the subject if defined.
        if (config.type === ChannelType.replayLastValue) {
            const lastValue = this.getSettings().channelLastValues[channelId];
            if (lastValue !== undefined) {
                receiverSubject.next(lastValue);
            }
        }
        // Listen to the custom event on window.
        this.subscriptions.push(fromEvent(window, eventType)
            .pipe(map((event) => event.detail))
            .subscribe((data) => receiverSubject.next(data)));
        return {
            emitter,
            receiver: receiverSubject.asObservable(),
        };
    }
    getSettings() {
        const foundSettings = window.eventBus;
        if (foundSettings) {
            return foundSettings;
        }
        else {
            window.eventBus = {
                channelConfigs: {},
                channelLastValues: {},
            };
            return this.getSettings();
        }
    }
}
EventBusService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EventBusService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
EventBusService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EventBusService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EventBusService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Generated bundle index. Do not edit.
 */

export { ChannelType, EventBusService };
